import { Moon, Sun } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/admin/use-theme';

type Appearance = 'light' | 'dark';

/**
 * Light/dark switch for the sidebar. Expanded, it's a pill-shaped toggle: a
 * thumb slides between the Sun (light) and Moon (dark) ends and carries the
 * active icon — Sun stays neutral foreground, Moon glows cobalt — while the
 * opposite icon sits faint on the track. Collapsed to the icon rail, it's a
 * single tap-to-flip icon button.
 *
 * The store theme can be 'system'; we resolve it to the *applied* appearance
 * (the `dark` class on <html>) so the switch reads correctly on first load.
 * Flipping writes an explicit 'light'/'dark'.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const reduced = useReducedMotion();

  const active: Appearance =
    theme === 'system'
      ? typeof document !== 'undefined' &&
        document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'light'
      : theme;

  const isDark = active === 'dark';
  const slide = reduced
    ? { duration: 0 }
    : ({ type: 'spring', stiffness: 380, damping: 32 } as const);

  const flip = () => setTheme(isDark ? 'light' : 'dark');

  return (
    <>
      {/* Expanded: sun/moon toggle */}
      <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label="Toggle dark mode"
        onClick={flip}
        className="relative flex h-7 w-14 items-center rounded-full border border-border bg-card px-1 group-data-[collapsible=icon]:hidden"
      >
        {/* Faint track icons — the state the thumb is NOT covering. */}
        <Sun
          aria-hidden
          className="absolute left-1.5 size-3.5 text-muted-foreground/50"
        />
        <Moon
          aria-hidden
          className="absolute right-1.5 size-3.5 text-muted-foreground/50"
        />
        {/* Sliding thumb carrying the active icon. */}
        <motion.span
          layout
          transition={slide}
          aria-hidden
          className={cn(
            'z-10 flex size-5 items-center justify-center rounded-full border border-border bg-secondary shadow-premium',
            isDark ? 'ml-auto' : 'mr-auto'
          )}
        >
          {isDark ? (
            <Moon className="size-3 text-cobalt dark:drop-shadow-[0_0_5px_var(--gl-cobalt)]" />
          ) : (
            <Sun className="size-3 text-foreground" />
          )}
        </motion.span>
      </button>

      {/* Collapsed (icon rail): a single button that flips the theme */}
      <button
        type="button"
        onClick={flip}
        title="Toggle theme"
        className="mx-auto hidden size-8 items-center justify-center rounded-md transition-colors duration-200 ease-[var(--ease-premium)] hover:bg-accent group-data-[collapsible=icon]:flex"
      >
        {isDark ? (
          <Moon className="size-4 text-cobalt dark:drop-shadow-[0_0_5px_var(--gl-cobalt)]" />
        ) : (
          <Sun className="size-4 text-foreground" />
        )}
        <span className="sr-only">Toggle theme</span>
      </button>
    </>
  );
}
